import React, { useState } from 'react';
import { useLogin } from '@refinedev/core';
import { Card, Form, Input, Button, Typography, Alert, Space } from 'antd';
import { LockOutlined, LoginOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

export const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mutate: login } = useLogin();

  const onFinish = async (values: { jwt: string }) => {
    setLoading(true);
    setError(null);

    try {
      await login(values, {
        onSuccess: () => {
          setError(null);
        },
        onError: (error) => {
          const errorMessage = error?.message || 'Login failed. Please check your JWT token.';
          setError(errorMessage);
        },
      });
    } finally {
      setLoading(false);
    }
  };

  // JWT validation - basic format check
  const validateJWT = (_: unknown, value: string) => {
    if (!value || value.trim() === '') {
      return Promise.reject(new Error('Please enter your JWT token'));
    }

    // Basic JWT format validation (3 parts separated by dots)
    const parts = value.trim().split('.');
    if (parts.length !== 3) {
      return Promise.reject(new Error('Invalid JWT format. Expected format: header.payload.signature'));
    }

    return Promise.resolve();
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Card style={{ width: 450, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <LockOutlined style={{ fontSize: 48, color: '#667eea', marginBottom: 16 }} />
            <Title level={2} style={{ marginBottom: 8 }}>
              Isola Dashboard
            </Title>
            <Text type="secondary">Ephemeral Test Environment Manager</Text>
          </div>

          {error && (
            <Alert
              message="Authentication Failed"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
            />
          )}

          <Form layout="vertical" onFinish={onFinish}>
            <Form.Item
              name="jwt"
              label="JWT Token"
              rules={[{ validator: validateJWT }]}
              extra="Obtain your JWT token from your identity provider (Okta, etc.)"
            >
              <Input.TextArea
                placeholder="eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9..."
                rows={6}
                disabled={loading}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                size="large"
                icon={<LoginOutlined />}
              >
                {loading ? 'Authenticating...' : 'Sign In'}
              </Button>
            </Form.Item>
          </Form>

          {import.meta.env.DEV && (
            <Alert
              message="Development Mode"
              description="For local testing, the Control Plane API should be running on http://localhost:8000"
              type="info"
              showIcon
            />
          )}
        </Space>
      </Card>
    </div>
  );
};
